import { createRoot } from 'react-dom/client';
import { Provider, useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import { ThemeProvider } from 'styled-components';
import Store from './store';
import CsRouter from './router/csRouter';
import AdminDataInitializer from '@copilot/common/utils/dataInitializer/admin';
import { PermissionsProvider } from '@copilot/common/hooks/permission';
import AppDataInitializer from '@copilot/common/utils/dataInitializer/app';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { appInsights } from '@copilot/common/components/snippets/applicationInsights';
import ErrorBoundary from '@copilot/common/components/containers/errorBoundary';
import { IntercomProvider } from 'react-use-intercom';
import { AntdTheme, Config, Theme } from '@copilot/common/config';
import AuthenticationProvider from '@copilot/common/utils/authenticationProvider';
import Apollo from '@copilot/common/components/snippets/apollo';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { CS_DASH_LAUNCH_DARKLY_ID, WELL_KNOWN_TENANT } from '@copilot/data/config/routes';
import LaunchDarklyIdentifier from './components/snippets/launchDarklyIdentifier';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { BrowserRouter } from 'react-router-dom';

// Wrapper component to provide LaunchDarkly identification
function CSAppWithLDIdentification({ children }: { children: React.ReactNode }) {
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    return (
        <>
            <LaunchDarklyIdentifier activeMember={activeMember ?? undefined} />
            {children}
        </>
    );
}

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');

const root = createRoot(container);

root.render(
    <Provider store={Store.ReduxStore}>
        <LDProvider
            clientSideID={CS_DASH_LAUNCH_DARKLY_ID}
            context={{
                key: 'anonymous',
                custom: {
                    well_known_tenant: WELL_KNOWN_TENANT,
                },
            }}
        >
            <BrowserRouter>
                <ConfigProvider prefixCls="copilot" theme={AntdTheme}>
                    <ThemeProvider theme={Theme}>
                        <AppInsightsContext.Provider value={appInsights.reactPlugin}>
                            {/** TODO [Copilot-4529] Intercom is required due to Nurturelist being shared. We should remove this dependency since CS Dash should not require intercom integration. */}
                            <IntercomProvider appId={Config.intercomAppId}>
                                <AuthenticationProvider>
                                    <AppDataInitializer>
                                        <Apollo>
                                            <PermissionsProvider>
                                                <AdminDataInitializer>
                                                    <CSAppWithLDIdentification>
                                                        <ErrorBoundary message="Something went wrong. Please refresh the page and try again">
                                                            <CsRouter />
                                                        </ErrorBoundary>
                                                    </CSAppWithLDIdentification>
                                                </AdminDataInitializer>
                                            </PermissionsProvider>
                                        </Apollo>
                                    </AppDataInitializer>
                                </AuthenticationProvider>
                            </IntercomProvider>
                        </AppInsightsContext.Provider>
                    </ThemeProvider>
                </ConfigProvider>
            </BrowserRouter>
        </LDProvider>
    </Provider>
);
